import { Input, Typography, UtilityButton } from '@quickbit/qb-design-system';
import { QRCodeCanvas } from 'qrcode.react';
import { useState } from 'react';
import { useAsyncFn, useMount } from 'react-use';
import { fetchAPI } from 'api';
import { GeneralErrorResponse } from 'api/fetchAPI';
import { Layout } from 'components';

export const AuthenticatorRecovery = () => {
  const [token, setToken] = useState(
    () => new URLSearchParams(window.location.hash.slice(1)).get('token') ?? ''
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [setupUrl, setSetupUrl] = useState('');
  const [message, setMessage] = useState('');
  const [complete, setComplete] = useState(false);
  useMount(() => {
    if (window.location.hash)
      window.history.replaceState(null, '', window.location.pathname);
  });
  const [{ loading, error }, submit] = useAsyncFn(async () => {
    if (!token) {
      const response = await fetchAPI<{ message: string }>(
        'authenticator/recovery/request',
        { method: 'POST', body: { email, password } }
      );
      setMessage(response.message);
      setPassword('');
      return;
    }
    const response = await fetchAPI<{ url?: string; success?: boolean }>(
      'authenticator/recovery/enroll',
      {
        method: 'POST',
        body: { token, password, ...(setupUrl ? { code } : {}) },
      }
    );
    if (response.url) setSetupUrl(response.url);
    if (response.success) {
      setToken('');
      setPassword('');
      setCode('');
      setSetupUrl('');
      setComplete(true);
    }
  }, [token, email, password, setupUrl, code]);

  return (
    <Layout title="Recover authenticator" backNavigationPath="/login">
      {complete ? (
        <>
          <Typography variant="body">
            Your new authenticator is enabled. Existing sessions have been
            signed out. Sign in using your password and new authenticator code.
          </Typography>
          <a href="/login/email">Return to login</a>
        </>
      ) : (
        <>
          <Typography variant="body">
            Use your account password and existing verified email to recover
            access. If you forgot your password, reset it first. If you cannot
            access your verified email, contact support for identity
            verification.
          </Typography>
          <a href="/login/forgot-password">Forgot password?</a>
          {!token && (
            <Input
              label="Account email"
              value={email}
              type="email"
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
          <Input
            label="Account password"
            value={password}
            type="password"
            onChange={(event) => setPassword(event.target.value)}
          />
          {setupUrl && (
            <>
              <Typography variant="body">
                Scan this private QR code with your authenticator app, then
                enter its current six-digit code. Your old authenticator remains
                active until you confirm.
              </Typography>
              <QRCodeCanvas value={setupUrl} size={200} />
              <a href={setupUrl}>Add Quickbit to your authenticator</a>
              <Input
                label="Authenticator code"
                value={code}
                onChange={(event) =>
                  setCode(event.target.value.replace(/\D/g, '').slice(0, 6))
                }
              />
            </>
          )}
          <UtilityButton
            text={
              token
                ? setupUrl
                  ? 'Confirm recovery'
                  : 'Set up new authenticator'
                : 'Email recovery link'
            }
            onClick={submit}
            disabled={
              loading ||
              !password ||
              (!token && !email) ||
              (!!setupUrl && code.length !== 6)
            }
          />
          {message && <Typography variant="body">{message}</Typography>}
          {error && (
            <Typography variant="body">
              {(error as GeneralErrorResponse).reason ??
                'Recovery failed. Request a new link or contact support.'}
            </Typography>
          )}
        </>
      )}
    </Layout>
  );
};
